import { Component, Input, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import DataTable from 'datatables.net-dt';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { checkIsDemoOrg } from 'src/app/core/models/organizations';
import { ReportJobService, ReportJobConfig } from 'src/app/core/services/reporting/report-job.service';

@Component({
  selector: 'app-events-escalations-dialog',
  standalone: true,
  imports: [CommonModule, DialogsModule, LayoutModule, ButtonsModule, NotificationModule],
  templateUrl: './events-escalations-dialog.component.html',
  styleUrls: ['./events-escalations-dialog.component.scss']
})
export class EventsEscalationsDialogComponent implements OnInit, OnDestroy {
  @Input() visible = false;
  @Input() organizationId!: number;
  @Input() startDate!: string; // ISO
  @Input() endDate!: string; // ISO
  @Input() securityTool?: string; // comma-separated product IDs

  @Output() close = new EventEmitter<void>();

  private eventsTable: any;
  private escalationsTable: any;

  /** Currently selected tab: 0 = Events (alerts), 1 = Escalations (tickets). */
  public selectedTabIndex = 0;

  /** Whether a CSV export is currently being queued. */
  public isExporting = false;

  /** SOC report type key per tab, for the server-side CSV export. */
  private readonly reportKeyByTab: Record<number, string> = {
    0: 'events-vs-escalations-events-tab-soc-report',
    1: 'events-vs-escalations-escalations-tab-soc-report',
  };

  constructor(
    private http: HttpClient,
    private reportJobService: ReportJobService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    if (this.visible) {
      queueMicrotask(() => {
        this.initEventsTable(); // default visible tab
      });
    }
  }

  ngOnDestroy(): void {
    this.destroyTables();
  }

  onDialogClose(): void {
    this.destroyTables();
    this.close.emit();
  }

  private destroyTables(): void {
    try { this.eventsTable?.destroy(); } catch {}
    try { this.escalationsTable?.destroy(); } catch {}
    this.eventsTable = null;
    this.escalationsTable = null;
  }

  private baseParams(): Record<string, string> {
    const params: Record<string, string> = {
      org_ids: String(this.organizationId),
      type: 'filtered',
      current_start: this.startDate,
      current_end: this.endDate,
    };
    if (this.securityTool) {
      params['security_tool'] = this.securityTool;
    }
    return params;
  }

  private initEventsTable(): void {
    if (this.eventsTable) {
      try { this.eventsTable.columns?.adjust?.().draw(false); } catch {}
      return;
    }
    let demostr = checkIsDemoOrg()? "/demo": "";
    const url = `${environment.apiUrl}${demostr}/v1/analytics/administrative/alerteventsdetails/`;
    const table = new (DataTable as any)('#eventsTable', {
      serverSide: true,
      processing: true,
      searching: true,
      lengthMenu: [10, 25, 50, 100, 200],
      ajax: (dtParams: any, callback: (data: any) => void) => {
        const q: Record<string, string> = {
          ...this.baseParams(),
          event: 'raw_events',
          '$count': 'true',
          '$top': String(dtParams.length || 10),
          '$skip': String(dtParams.start || 0),
        };
        // Ordering mapping
        if (dtParams.order?.length) {
          const ord = dtParams.order[0];
          const col = dtParams.columns[ord.column];
          if (col?.data) {
            q['$orderby'] = `${col.data} ${ord.dir}`;
          }
        }

        const params = new HttpParams({ fromObject: q });
        this.http.get<any>(url, { params }).subscribe({
          next: (json) => {
            callback({
              draw: dtParams.draw,
              recordsTotal: json['@odata.count'] ?? 0,
              recordsFiltered: json['@odata.count'] ?? 0,
              data: json.value ?? [],
            });
          },
          error: () => callback({ draw: dtParams.draw, recordsTotal: 0, recordsFiltered: 0, data: [] }),
        });
      },
      columns: [
        { data: 'event_name', title: 'Event', name: 'event_name' },
        { data: 'customer', title: 'Customer', name: 'customer' },
        { data: 'case_id', title: 'SOAR ID', name: 'case_id' },
        { data: 'source_ip', title: 'Source IP', name: 'source_ip' },
        { data: 'destination_ip', title: 'Destination IP', name: 'destination_ip' },
        { data: 'case_severity', title: 'Severity', name: 'case_severity' },
        { data: 'zoho_ticket_number', title: 'Escalated Case #', name: 'zoho_ticket_number' },
        { data: 'closure_reason', title: 'Triaged Result', name: 'closure_reason' },
        { data: 'mitre_tactic', title: 'Mitre Tactic', name: 'mitre_tactic' },
        { data: 'event_occurred_on', title: 'Raw Event Occurred', name: 'event_occurred_on' },
        { data: 'alert_received_on', title: 'Alert Received', name: 'alert_received_on' },
        { data: 'case_created_on', title: 'Case Created On', name: 'case_created_on' },
        { data: 'case_closed_on', title: 'Case Closed', name: 'case_closed_on' },
        { data: 'triage_type', title: 'Triage Type', name: 'triage_type' },
        { data: 'case_source', title: 'Source', name: 'case_source' },
        { data: 'close_reason_justification', title: 'Closure Details', name: 'close_reason_justification' },
        { data: 'case_tt_closed_min', title: 'MTTD (min)', name: 'case_tt_closed_min' },
        { data: 'tracking_id', title: 'Raw Event ID', name: 'tracking_id' },
        { data: 'sla_exceeded', title: 'SLA Exceeded', name: 'sla_exceeded' },
        { data: 'parent_name', title: 'Parent Account Name', name: 'parent_name' },
        { data: 'industry_name', title: 'Industry', name: 'industry_name' },
        { data: 'tags', title: 'Tags', name: 'tags' },
      ],
    });
    this.eventsTable = table;
  }

  private initEscalationsTable(): void {
    if (this.escalationsTable) {
      try { this.escalationsTable.columns?.adjust?.().draw(false); } catch {}
      return;
    }
    let demostr = checkIsDemoOrg()? "/demo": "";
    const url = `${environment.apiUrl}${demostr}/v1/analytics/administrative/ticketeventsdetails/`;
    const table = new (DataTable as any)('#escalationsTable', {
      serverSide: true,
      processing: true,
      searching: true,
      // Default UI sort: Created On descending (column index 2)
      order: [[2, 'desc']],
      lengthMenu: [10, 25, 50, 100, 200],
      ajax: (dtParams: any, callback: (data: any) => void) => {
        // Build params specifically for ticket endpoint using created_time_* (no current_* keys)
        const q: Record<string, string> = {
          org_ids: String(this.organizationId),
          type: 'filtered',
          created_time_after: this.startDate,
          created_time_before: this.endDate,
          event: 'escalations',
          '$filter': "department_name eq 'SOC Incidents' and ticket_status ne null",
          '$count': 'true',
          '$top': String(dtParams.length || 10),
          '$skip': String(dtParams.start || 0),
        };
        if (this.securityTool) {
          q['security_tool'] = this.securityTool;
        }
        if (dtParams.order?.length) {
          const ord = dtParams.order[0];
          const col = dtParams.columns[ord.column];
          if (col?.data) {
            q['$orderby'] = `${col.data} ${ord.dir}`;
          }
        } else {
          // Default API sort if UI didn't send one
          q['$orderby'] = 'created_time desc';
        }
        const params = new HttpParams({ fromObject: q });
        this.http.get<any>(url, { params }).subscribe({
          next: (json) => {
            const total = json['@odata.count'] ?? json.recordsTotal ?? 0;
            let rows = json.value ?? json.data ?? [];
            // Transform MTTR from minutes to hours
            rows = rows.map((item: any) => ({
              ...item,
              mean_time_to_resolve: item.mean_time_to_resolve !== null && item.mean_time_to_resolve !== undefined
                ? parseFloat((item.mean_time_to_resolve / 60).toFixed(1))
                : null
            }));
            callback({ draw: dtParams.draw, recordsTotal: total, recordsFiltered: total, data: rows });
          },
          error: () => callback({ draw: dtParams.draw, recordsTotal: 0, recordsFiltered: 0, data: [] }),
        });
      },
      columns: [
        { data: 'customer', title: 'Customer', name: 'customer' },
        { data: 'request_id', title: 'Escalated Case #', name: 'request_id' },
        { data: 'created_time', title: 'Created On', name: 'created_time' },
        { data: 'subject', title: 'Event Name', name: 'subject' },
        { data: 'priority', title: 'Severity', name: 'priority' },
        { data: 'ticket_source', title: 'Event Source', name: 'ticket_source' },
        { data: 'ticket_status', title: 'SOC Status', name: 'ticket_status' },
        { data: 'customer_defined_status', title: 'Customer Status', name: 'customer_defined_status' },
        { data: 'sla_violation_type', title: 'SLA Violation', name: 'sla_violation_type' },
        { data: 'ticket_closed_time', title: 'Closed On', name: 'ticket_closed_time' },
        { data: 'mean_time_to_resolve', title: 'MTTR (hrs)', name: 'mean_time_to_resolve' },
        { data: 'parent_name', title: 'Parent Account Name', name: 'parent_name' },
        { data: 'industry_name', title: 'Industry', name: 'industry_name' },
      ],
    });
    this.escalationsTable = table;
  }

  // Triggered when user switches tabs; initialize tables lazily
  onTabSelect(e: any): void {
    const index = e?.index ?? 0;
    this.selectedTabIndex = index;
    // Each tab is a fresh export context — clear any leftover in-flight state so a
    // stuck/incomplete export from the previous tab doesn't keep the button disabled.
    this.isExporting = false;
    if (index === 0) {
      this.initEventsTable();
    } else if (index === 1) {
      this.initEscalationsTable();
    }
  }

  /**
   * Queue a server-side CSV export for whichever tab is currently shown.
   * Resolves the report template on demand, so a tab whose report type is not
   * configured yet (e.g. Events) surfaces a friendly message instead of failing.
   */
  triggerExport(): void {
    const reportKey = this.reportKeyByTab[this.selectedTabIndex];

    if (!reportKey || !this.organizationId || !this.startDate || !this.endDate) {
      this.notificationService.show({
        content: 'Unable to export: missing organization or date range.',
        type: { style: 'warning', icon: true },
        position: { horizontal: 'right', vertical: 'top' },
        animation: { type: 'fade', duration: 400 },
        hideAfter: 5000,
      });
      return;
    }

    this.isExporting = true;

    const url = `${environment.apiUrl}/v2/reporting/api/report-types/?$filter=key eq '${reportKey}'`;
    this.http
      .get<{ value: any[] }>(url)
      .pipe(map((response) => response.value?.[0]?.report_template?.id ?? null))
      .subscribe({
        next: (templateId) => {
          if (!templateId) {
            this.isExporting = false;
            this.notificationService.show({
              content: 'CSV export is not available for this view yet.',
              type: { style: 'warning', icon: true },
              position: { horizontal: 'right', vertical: 'top' },
              animation: { type: 'fade', duration: 400 },
              hideAfter: 5000,
            });
            return;
          }

          const config: ReportJobConfig = {
            organizations: [this.organizationId],
            primary_organization_id: this.organizationId,
            report_template_id: templateId,
            start_date: formatDate(this.startDate, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
            end_date: formatDate(this.endDate, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
            delivery_method: 'download',
            report_params: {
              filter_type: 'filtered',
              ...(this.securityTool ? { security_tool: this.securityTool } : {}),
            },
          };

          this.reportJobService.createReportJob(config).subscribe({
            next: () => {
              this.isExporting = false;
              this.notificationService.show({
                content: 'Export queued. You will find the CSV in the Reports history once generated.',
                type: { style: 'success', icon: true },
                position: { horizontal: 'right', vertical: 'top' },
                animation: { type: 'fade', duration: 400 },
                closable: true,
                hideAfter: 5000,
              });
            },
            error: () => {
              this.isExporting = false;
              this.notificationService.show({
                content: 'Failed to queue export. Please try again.',
                type: { style: 'error', icon: true },
                position: { horizontal: 'right', vertical: 'top' },
                animation: { type: 'fade', duration: 400 },
                hideAfter: 5000,
              });
            },
          });
        },
        error: () => {
          this.isExporting = false;
          this.notificationService.show({
            content: 'Failed to queue export. Please try again.',
            type: { style: 'error', icon: true },
            position: { horizontal: 'right', vertical: 'top' },
            animation: { type: 'fade', duration: 400 },
            hideAfter: 5000,
          });
        },
      });
  }
}
